import {GetCurrentGwGAcceleratorEnrollmentsQuery} from '@udemy/career-academies';
import {
    useConsumerSubscriptionGwGPlan,
    useConsumerSubscriptionGwGPlusPersonalPlan,
} from '@udemy/subscription-graphql';

type GwgPlan = ReturnType<typeof useConsumerSubscriptionGwGPlan>;
type CombinedGwgPlan = ReturnType<typeof useConsumerSubscriptionGwGPlusPersonalPlan>;

/**
 * Helper to check if subscription data is still loading.
 * Returns true only if either plan is explicitly loading.
 * Note: subscription-graphql returns an object with isLoading=false when plan is unavailable,
 * so we only check the isLoading property when the object exists.
 */
export const isSubscriptionLoading = (
    gwgPlan: GwgPlan,
    combinedGwgPlan: CombinedGwgPlan,
): boolean => {
    return !!(gwgPlan?.isLoading || combinedGwgPlan?.isLoading);
};

/**
 * Helper to check if GwG plans are available (feature is enabled for this user/region).
 */
export const checkIsGwgAvailable = (
    gwgPlan: GwgPlan,
    combinedGwgPlan: CombinedGwgPlan,
): boolean => {
    const plans = [gwgPlan, combinedGwgPlan];
    return plans.some(
        (plan) => !!(plan && !plan.isLoading && ('id' in plan || plan.isSubscriberToSpecificPlan)),
    );
};

/**
 * Helper to check if user is an active GwG subscriber (either GwG-only or PP+GwG).
 */
export const checkIsGwgSubscriber = (
    gwgPlan: GwgPlan,
    combinedGwgPlan: CombinedGwgPlan,
): boolean => {
    return (
        (gwgPlan &&
            !gwgPlan.isLoading &&
            'isSubscriberToSpecificPlan' in gwgPlan &&
            gwgPlan.isSubscriberToSpecificPlan) ||
        (combinedGwgPlan &&
            !combinedGwgPlan.isLoading &&
            'isSubscriberToSpecificPlan' in combinedGwgPlan &&
            combinedGwgPlan.isSubscriberToSpecificPlan) ||
        false
    );
};

/**
 * Helper to check if user has combined Personal Plan + GwG subscription (not GwG-only).
 */
export const checkIsCombinedPPGwgSubscriber = (combinedGwgPlan: CombinedGwgPlan): boolean => {
    return !!(
        combinedGwgPlan &&
        !combinedGwgPlan.isLoading &&
        'isSubscriberToSpecificPlan' in combinedGwgPlan &&
        combinedGwgPlan.isSubscriberToSpecificPlan
    );
};

/**
 * Helper to check if user is enrolled in a GwG accelerator (learning path).
 * Filters by isInGooglePartnerCollection to identify GwG-specific accelerators.
 */
export const checkIsGwgEnrolled = (
    enrollmentData: GetCurrentGwGAcceleratorEnrollmentsQuery | undefined,
): boolean => {
    const enrollments =
        enrollmentData?.me?.enrollments?.careerAcceleratorEnrollments?.enrollments ?? [];
    return enrollments.some(
        (enrollment) =>
            enrollment?.careerAccelerator?.contentCollectionInfo?.isInGooglePartnerCollection ===
            true,
    );
};
